import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  Check,
  Edit3,
  Camera,
  Copy,
  TrendingUp,
  Wallet,
  ArrowRightLeft,
  Award,
  ChevronRight,
  BadgeCheck,
  Lock,
  Smartphone,
  Settings,
  Star,
  Globe,
  ArrowDownLeft,
  ArrowUpRight,
  Search,
  AlertCircle,
  LayoutDashboard,
  Activity,
  Users,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn, getActivationDate } from "@/lib/utils";
import { useMyTrades, useProfile, useUserReputationScore } from "@/hooks";
import { KycStatus } from "@/interfaces/kyc";
import { Input } from "@/components/ui/input";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { copyToClipboard, formatCurrency } from "@/utils";
import { type Trade } from "@/interfaces/trade";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import TransactionReceipt from "@/components/user/TransactionReceipt";
import { TransactionRow } from "@/components/user/TransactionRow";
import type {
  FormattedTransaction,
  FormattedTransactionInterface,
} from "@/interfaces/transaction";
import { makeRequest } from "@/utils/makeRequest";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import LeaveReviewDialog from "@/components/p2p/merchant/reviews/LeaveReviewDialog";
import { useAppSelector } from "@/store/hooks";
import TradeCard from "@/components/shared/TradeCard";

const UserDashboard = () => {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id || "";

  const { data: profile, isError } = useProfile(userId);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [tradeFilter, setTradeFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState<FormattedTransaction | null>(
    null,
  );
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openReview, setOpenReview] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);

  console.log("profile", profile);

  const [page, setPage] = useState(1);

  const { data: userScore, isLoading: isLoadingScore } =
    useUserReputationScore(userId);

  const fetchTransactions = async () => {
    const res = await makeRequest.get("/transaction/me", {
      params: { page, limit: 10 },
    });
    return res.data;
  };

  const {
    data: userTransactions,
    isLoading,

    refetch,
  } = useQuery<FormattedTransactionInterface>({
    queryKey: ["transactions", user?.id, page],
    queryFn: fetchTransactions,
    enabled: !!user?.id,
  });

  const transactions: FormattedTransaction[] = userTransactions?.data ?? [];
  const meta = userTransactions?.meta;

  const filteredTransactions = useMemo(() => {
    const searchTerm = search.toLowerCase().trim();
    return transactions.filter((tx) => {
      const matchesSearch =
        !searchTerm ||
        tx.id.toLowerCase().includes(searchTerm) ||
        tx.message?.toLowerCase().includes(searchTerm) ||
        tx.counterparty.name.toLowerCase().includes(searchTerm);

      const matchesType = typeFilter === "all" || tx.type === typeFilter;
      const matchesStatus =
        statusFilter === "all" || tx.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [transactions, search, typeFilter, statusFilter]);

  const openReceipt = (tx: FormattedTransaction) => {
    setSelectedTx(tx);
    setIsModalOpen(true);
  };

  const {
    data: myTrades,
    isLoading: isLoadingMyTrades,
    // isError,
    // refetch,
  } = useMyTrades(userId as string);

  const trades = myTrades ?? [];

  const winRate =
    (profile?.disputesWon ?? 0) + (profile?.disputesLost ?? 0) > 0
      ? ((profile?.disputesWon ?? 0) /
          ((profile?.disputesWon ?? 0) + (profile?.disputesLost ?? 0))) *
        100
      : 0;

  const completionChecks = [
    {
      completed: profile?.emailVerified,
      weight: 15,
    },
    // {
    //   completed: profile?.phoneVerified,
    //   weight: 15,
    // },
    {
      completed: profile?.kycStatus === KycStatus.APPROVED,
      weight: 25,
    },
    // {
    //   completed: !!profile?.avatar,
    //   weight: 10,
    // },
    {
      completed: profile?.firstName && profile?.lastName,
      weight: 15,
    },
    {
      completed: (profile?.paymentMethods?.length ?? 0) > 0,
      weight: 15,
    },
    {
      completed: profile?.setting?.twoFAEnabled,
      weight: 15,
    },
    {
      completed: profile?.isSecurityCodeVerified,
      weight: 15,
    },
  ];

  const profileCompletion = completionChecks.reduce(
    (sum, check) => sum + (check.completed ? check.weight : 0),
    0,
  );

  const filteredTrades = useMemo(() => {
    return trades.filter((trade) => {
      const matchesSearch =
        trade.id.toLowerCase().includes(search.toLowerCase()) ||
        trade.seller?.username.toLowerCase().includes(search.toLowerCase()) ||
        trade.buyer?.username.toLowerCase().includes(search.toLowerCase());

      const matchesFilter =
        tradeFilter === "all" ||
        trade.status.toLowerCase() === tradeFilter.toLowerCase();

      return matchesSearch && matchesFilter;
    });
  }, [search, tradeFilter, trades]);

  // const recentActivity = [
  //   {
  //     type: "trade",
  //     title: "Bought 0.5 BTC",
  //     time: "2 hours ago",
  //     amount: "$32,450.00",
  //     status: "completed",
  //   },
  //   {
  //     type: "deposit",
  //     title: "Deposit via Bank Transfer",
  //     time: "5 hours ago",
  //     amount: "$5,000.00",
  //     status: "completed",
  //   },
  //   {
  //     type: "withdrawal",
  //     title: "Withdrawal to Bank",
  //     time: "1 day ago",
  //     amount: "$1,200.00",
  //     status: "completed",
  //   },
  //   {
  //     type: "kyc",
  //     title: "KYC Level 2 Approved",
  //     time: "3 days ago",
  //     amount: null,
  //     status: "completed",
  //   },
  // ];

  const securityItems = [
    {
      label: "Email Verification",
      status: profile?.emailVerified ? "enabled" : "disabled",
      icon: Mail,
      url: "/dashboard/settings?tab=notifications",
    },
    {
      label: "Phone Verification",
      status: profile?.phoneVerified ? "enabled" : "disabled",
      icon: Smartphone,
      url: "/dashboard/settings?tab=security",
    },
    {
      label: "Two-Factor Auth",
      status: profile?.setting?.twoFAEnabled ? "enabled" : "disabled",
      icon: Lock,
      url: "/dashboard/settings?tab=security",
    },
    {
      label: "KYC Verification",
      status: profile?.kycStatus === KycStatus.APPROVED ? "enabled" : "pending",
      icon: BadgeCheck,
      url: "/dashboard/kyc",
    },
  ];

  const achievements = [
    {
      name: "First Trade",
      description: "Completed your first trade",
      earned: trades.length > 0,
      icon: TrendingUp,
    },
    {
      name: "Volume Master",
      description: "Traded over $10,000",
      earned: (userScore?.tradeVolume ?? 0) > 10000,
      icon: Award,
    },
    {
      name: "Security Pro",
      description: "Enabled all security features",
      earned: profile?.setting?.twoFAEnabled,
      icon: Shield,
    },
    {
      name: "KYC Verified",
      description: "Completed KYC verification",
      earned: (profile?.kycLevel ?? 0) > 0,
      icon: BadgeCheck,
    },
  ];

  if (isLoading || isLoadingScore) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background lg:px-6 pb-12 mt-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center mx-1 sm:justify-between gap-4 mb-8">
          <div className="flex gap-2 mt-4 items-center justify-between">
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard/settings")}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button
              className="rounded-full"
              onClick={() => navigate("/dashboard/settings")}
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        </div>

        {/* Main Profile Card */}
        <div className="flex flex-col gap-6 mb-8">
          {/* User Info Card */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-xl p-6 relative">
              <div className="relative w-28 h-28 mx-auto mb-4">
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-4xl">
                    {profile?.avatar || profile?.firstName?.[0] || "U"}
                  </span>
                </div>
                <button className="absolute bottom-0 right-0 w-8 h-8 bg-card border border-border rounded-full flex items-center justify-center hover:bg-accent transition-colors">
                  <Camera className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-foreground">
                  {profile?.firstName + " " + profile?.lastName || "User Name"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {profile?.email}
                </p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    <Star className="w-3 h-3 mr-1 text-primary" />
                    {profile?.advertiserTier} Level {profile?.kycLevel}
                  </Badge>
                  {profile?.kycStatus === KycStatus.APPROVED && (
                    <Badge className="bg-emerald-500/20 text-emerald-500 text-xs">
                      <BadgeCheck className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <div className="flex items-center justify-between w-full">
                  <div className="text-sm text-muted-foreground">UID</div>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-500 text-lg">
                      {profile?.uid || ""}
                    </span>
                    <button
                      onClick={() => copyToClipboard(profile?.id ?? "")}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Username
                  </span>
                  <div className="flex items-center gap-2">
                    <span className=" font-mono text-lg">
                      {profile?.merchantName || profile?.username || ""}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Member Since
                  </span>
                  <span className="text-sm text-foreground">
                    {getActivationDate(profile?.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Country</span>
                  <span className="text-sm text-foreground flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    {profile?.country?.name}
                  </span>
                </div>
              </div>

              <div className="pt-4 mt-4 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    Profile Completion
                  </span>
                  <span className="text-sm font-medium text-primary">
                    {profileCompletion} %
                  </span>
                </div>
                <Progress value={profileCompletion} className="h-2" />
              </div>

              <div className="mt-4 w-full flex flex-col gap-2">
                {/* {profile?.emailVerified && (
                  <div className="flex items-center gap-1 text-sm text-primary cursor-pointer hover:text-emerald-500">
                    <AlertCircle size="18"></AlertCircle>
                    Verify your email (+15%)
                  </div>
                )} */}
                {/* {!profile?.phoneVerified && (
                  <div className="flex items-center gap-1 text-sm text-primary cursor-pointer hover:text-emerald-500">
                    <AlertCircle size="18"></AlertCircle>
                    Verify your phone number (+15%)
                  </div>
                )} */}

                {profile?.kycStatus !== KycStatus.APPROVED && (
                  <Link
                    to="/dashboard/kyc"
                    className="flex items-center gap-1 text-sm text-primary cursor-pointer hover:text-emerald-500"
                  >
                    <AlertCircle size="18"></AlertCircle>
                    Complete identity verification (+25%)
                  </Link>
                )}

                {!profile?.setting?.twoFAEnabled && (
                  <Link
                    to="/dashboard/settings?tab=security"
                    className="flex items-center gap-1 text-sm text-primary cursor-pointer hover:text-emerald-500"
                  >
                    <AlertCircle size="18"></AlertCircle>
                    Enable Two-Factor Authentication (+15%)
                  </Link>
                )}
                {!profile?.isSecurityCodeVerified && (
                  <Link
                    to="/dashboard/settings?tab=security"
                    className="flex items-center gap-1 text-sm text-primary cursor-pointer hover:text-emerald-500"
                  >
                    <AlertCircle size="18"></AlertCircle>
                    Setup transaction password (+15%)
                  </Link>
                )}

                {(profile?.paymentMethods.length ?? 0) < 1 && (
                  <Link
                    to="/dashboard/settings?tab=payment-methods"
                    className="flex items-center gap-1 text-sm text-primary cursor-pointer hover:text-emerald-500"
                  >
                    <AlertCircle size="18"></AlertCircle>
                    Add a payment method (+15%)
                  </Link>
                )}
              </div>

              <div className="pointer-events-none absolute inset-0 flex items-center justify-center select-none overflow-hidden">
                <span className="text-[7rem] md:text-[9rem] lg:text-[11rem] font-black tracking-[-0.08em] leading-none text-foreground/[0.025] dark:text-white/[0.03] whitespace-nowrap uppercase scale-110">
                  BuyFiat
                </span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-xl p-4 mt-4">
              <h3 className="text-sm font-medium text-foreground mb-3">
                Quick Actions
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => navigate("/dashboard/kyc")}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <BadgeCheck className="w-5 h-5 text-primary" />
                    <span className="text-sm text-foreground">
                      KYC Verification
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
                <button
                  onClick={() => navigate("/dashboard/settings?tab=security")}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="text-sm text-foreground">Security</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>

                <button
                  onClick={() => navigate("/dashboard/settings")}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-primary" />
                    <span className="text-sm text-foreground">Settings</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
                <button
                  onClick={() => navigate("/dashboard/p2p")}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-primary" />
                    <span className="text-sm text-foreground">
                      P2P Merchant Dashboard
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
                <button
                  onClick={() => navigate("/dashboard/support")}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    <span className="text-sm text-foreground">Support</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="lg:col-span-2 min-w-0">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full mx-1"
            >
              <div className="relative mb-6">
                {/* Left Fade */}
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />

                {/* Right Fade */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />

                <div className="overflow-x-auto scrollbar-hide w-full">
                  <TabsList className="w-ful justify-start bg-transparent p-0 h-auto overflow-x-auto whitespace-nowrap scrollbar-hide rounded-none">
                    <div className="flex gap-2 min-w-max pb-2">
                      <TabsTrigger
                        value="overview"
                        className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Overview
                      </TabsTrigger>

                      <TabsTrigger
                        value="trades"
                        className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                      >
                        <TrendingUp className="mr-2 h-4 w-4" />
                        My Trades
                      </TabsTrigger>

                      <TabsTrigger
                        value="security"
                        className=" shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        Security
                      </TabsTrigger>

                      {/* <TabsTrigger
                        value="activity"
                        className="shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                      >
                        <Activity className="mr-2 h-4 w-4" />
                        Activity
                      </TabsTrigger> */}

                      <TabsTrigger
                        value="achievements"
                        className=" shrink-0 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium text-muted-foreground transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary data-[state=active]:shadow-lg hover:bg-muted hover:text-foreground"
                      >
                        <Award className="mr-2 h-4 w-4" />
                        Achievements
                      </TabsTrigger>
                    </div>
                  </TabsList>
                </div>
              </div>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                {/* Trading Stats */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">
                      Trading Statistics
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      Last 30 Days
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">
                        Total Trades
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        {userScore?.completedTrades ?? 0}
                      </p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">
                        Trading Volume
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        $ {formatCurrency(userScore?.tradeVolume30d ?? 0)}
                      </p>
                    </div>
                    {/* <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">P&L</p>
                      <p className="text-2xl font-bold text-emerald-500">
                        {tradingStats.profitLoss}
                      </p>
                    </div> */}
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">
                        Win Rate
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        {winRate.toFixed(2)}%
                      </p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">
                        Avg Trade Size
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        ${" "}
                        {formatCurrency(
                          trades.length > 0
                            ? Number(userScore?.tradeVolume ?? 0) /
                                trades.length
                            : 0,
                        )}
                      </p>
                    </div>
                    {/* <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">
                        Favorite Pair
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {tradingStats.favoritePair}
                      </p>
                    </div> */}
                  </div>
                </div>

                {/* Account Info */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Account Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                      <User className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Full Name
                        </p>
                        <p className="text-foreground font-medium">
                          {profile?.firstName + " " + profile?.lastName ||
                            "Not set"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-foreground font-medium">
                          {profile?.email}
                        </p>
                        {profile?.emailVerified && (
                          <span className="text-xs text-emerald-500 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="text-foreground font-medium">
                          {profile?.phoneNumber || "Not set"}
                        </p>
                        {profile?.phoneVerified && (
                          <span className="text-xs text-emerald-500 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Date of Birth
                        </p>
                        <p className="text-foreground font-medium">
                          {" "}
                          {
                            profile?.kycSubmissions[0]?.formData?.formData
                              ?.dateOfBirth
                          }{" "}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TRADES */}
              <TabsContent value="trades" className="-mx-4">
                <div className="rounded-2xl border bg-card p-6">
                  <div className="flex justify-between mb-6">
                    <div>
                      <h3 className="font-semibold text-lg">Live Trades</h3>

                      <p className="text-sm text-muted-foreground">
                        Recent activity
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <ArrowDownLeft className="text-emerald-500" />

                      <ArrowUpRight className="text-red-500" />
                    </div>
                  </div>

                  <div className="flex flex-col lg:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-3 h-6 w-6 text-muted-foreground" />

                      <Input
                        placeholder="Search trades..."
                        className="pl-10 h-14"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>

                    <Select value={tradeFilter} onValueChange={setTradeFilter}>
                      <SelectTrigger className="w-full !h-12 lg:w-40">
                        <SelectValue />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="PENDING">Pending</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="EXPIRED">Expired</SelectItem>
                        <SelectItem value="PAID">Paid</SelectItem>
                        <SelectItem value="CANCELLED">Cancelled</SelectItem>
                        <SelectItem value="DISPUTED">Disputed</SelectItem>
                        <SelectItem value="UNDER_REVIEW">
                          Under Review
                        </SelectItem>
                        <SelectItem value="REFUNDED">Refunded</SelectItem>
                        <SelectItem value="RELEASED">Released</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                    {filteredTrades.map((trade) => (
                      <TradeCard
                        key={trade.id}
                        trade={trade}
                        selectedTrade={selectedTrade}
                        setSelectedTrade={setSelectedTrade}
                        openReview={openReview}
                        setOpenReview={setOpenReview}
                      />
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Security Tab */}
              <TabsContent value="security" className="space-y-6">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">
                      Security Status
                    </h3>
                    <Badge className="bg-emerald-500/20 text-emerald-500">
                      Strong
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    {securityItems.map((item, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center",
                              item.status === "enabled"
                                ? "bg-emerald-500/20"
                                : "bg-amber-500/20",
                            )}
                          >
                            <item.icon
                              className={cn(
                                "w-5 h-5",
                                item.status === "enabled"
                                  ? "text-emerald-500"
                                  : "text-amber-500",
                              )}
                            />
                          </div>
                          <div>
                            <p className="text-foreground font-medium">
                              {item.label}
                            </p>
                            <p
                              className={cn(
                                "text-xs capitalize",
                                item.status === "enabled"
                                  ? "text-emerald-500"
                                  : "text-amber-500",
                              )}
                            >
                              {item.status}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(item.url)}
                        >
                          {item.status === "enabled" ? "Manage" : "Enable"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Login History */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Recent Login Activity
                  </h3>
                  <div className="space-y-3">
                    {[
                      {
                        device: "Chrome on Windows",
                        location: "Lagos, Nigeria",
                        time: "Just now",
                        current: true,
                      },
                      {
                        device: "Safari on iPhone",
                        location: "Lagos, Nigeria",
                        time: "2 days ago",
                        current: false,
                      },
                      {
                        device: "Firefox on Mac",
                        location: "Abuja, Nigeria",
                        time: "1 week ago",
                        current: false,
                      },
                    ].map((login, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Smartphone className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-foreground font-medium flex items-center gap-2">
                              {login.device}
                              {login.current && (
                                <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px]">
                                  Current
                                </Badge>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {login.location} • {login.time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Activity Tab */}
              {/* <TabsContent value="activity" className="space-y-6">
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Recent Activity
                  </h3>
                  <div className="space-y-3">
                    {recentActivity.map((activity, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center",
                              activity.type === "trade" && "bg-primary/10",
                              activity.type === "deposit" &&
                                "bg-emerald-500/10",
                              activity.type === "withdrawal" &&
                                "bg-amber-500/10",
                              activity.type === "kyc" && "bg-blue-500/10",
                            )}
                          >
                            {activity.type === "trade" && (
                              <TrendingUp className="w-5 h-5 text-primary" />
                            )}
                            {activity.type === "deposit" && (
                              <Wallet className="w-5 h-5 text-emerald-500" />
                            )}
                            {activity.type === "withdrawal" && (
                              <ArrowRightLeft className="w-5 h-5 text-amber-500" />
                            )}
                            {activity.type === "kyc" && (
                              <BadgeCheck className="w-5 h-5 text-blue-500" />
                            )}
                          </div>
                          <div>
                            <p className="text-foreground font-medium">
                              {activity.title}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {activity.time}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {activity.amount && (
                            <p className="text-foreground font-medium">
                              {activity.amount}
                            </p>
                          )}
                          <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px]">
                            {activity.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={() => navigate("/dashboard/transactions")}
                  >
                    View All Activity
                  </Button>
                </div>
              </TabsContent> */}

              {/* Achievements Tab */}
              <TabsContent value="achievements" className="space-y-6">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">
                      Achievements
                    </h3>
                    <Badge variant="outline">
                      {achievements.filter((a) => a.earned).length}/
                      {achievements.length} Earned
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {achievements.map((achievement, i) => (
                      <div
                        key={i}
                        className={cn(
                          "p-4 rounded-lg border transition-all",
                          achievement.earned
                            ? "bg-primary/5 border-primary/30"
                            : "bg-muted/30 border-border opacity-60",
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              "w-12 h-12 rounded-xl flex items-center justify-center",
                              achievement.earned ? "bg-primary/20" : "bg-muted",
                            )}
                          >
                            <achievement.icon
                              className={cn(
                                "w-6 h-6",
                                achievement.earned
                                  ? "text-primary"
                                  : "text-muted-foreground",
                              )}
                            />
                          </div>
                          <div>
                            <p
                              className={cn(
                                "font-medium",
                                achievement.earned
                                  ? "text-foreground"
                                  : "text-muted-foreground",
                              )}
                            >
                              {achievement.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {achievement.description}
                            </p>
                            {achievement.earned && (
                              <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px] mt-2">
                                <Check className="w-3 h-3 mr-1" /> Earned
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Transactions Card */}
        <Card className="shadow-sm overflow-hidden">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-4 w-full">
              <span>Recent Transactions</span>
              <Link to="/dashboard/transactions">
                <Button className="rounded-full">View All</Button>
              </Link>
            </CardTitle>
          </CardHeader>

          <CardContent className="p-0">
            <Tabs defaultValue="all">
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-6">
                <TabsTrigger value="all" className="py-3">
                  All
                </TabsTrigger>
                <TabsTrigger value="in" className="py-3">
                  Deposits
                </TabsTrigger>
                <TabsTrigger value="out" className="py-3">
                  Withdrawals
                </TabsTrigger>
              </TabsList>

              {/* ALL TRANSACTIONS */}
              <TabsContent value="all" className="m-0">
                <div className="divide-y">
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((tx) => (
                      <TransactionRow
                        key={tx.id}
                        tx={tx}
                        onClick={() => openReceipt(tx)}
                        onCopy={(e) => {
                          e.stopPropagation();
                          copyToClipboard(tx.id);
                        }}
                      />
                    ))
                  ) : (
                    <div className="p-12 text-center text-muted-foreground">
                      No transactions found matching your criteria.
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* DEPOSITS (IN) */}
              <TabsContent value="in" className="m-0">
                <div className="divide-y">
                  {filteredTransactions.filter((tx) => tx.direction === "IN")
                    .length > 0 ? (
                    filteredTransactions
                      .filter((tx) => tx.direction === "IN")
                      .map((tx) => (
                        <TransactionRow
                          key={tx.id}
                          tx={tx}
                          onClick={() => openReceipt(tx)}
                          onCopy={(e) => {
                            e.stopPropagation();
                            copyToClipboard(tx.id);
                          }}
                        />
                      ))
                  ) : (
                    <div className="p-12 text-center text-muted-foreground">
                      No deposits found.
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* WITHDRAWALS (OUT) */}
              <TabsContent value="out" className="m-0">
                <div className="divide-y">
                  {filteredTransactions.filter((tx) => tx.direction === "OUT")
                    .length > 0 ? (
                    filteredTransactions
                      .filter((tx) => tx.direction === "OUT")
                      .map((tx) => (
                        <TransactionRow
                          key={tx.id}
                          tx={tx}
                          onClick={() => openReceipt(tx)}
                          onCopy={(e) => {
                            e.stopPropagation();
                            copyToClipboard(tx.id);
                          }}
                        />
                      ))
                  ) : (
                    <div className="p-12 text-center text-muted-foreground">
                      No withdrawals found.
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <TransactionReceipt
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          selectedTx={selectedTx}
        />

        <LeaveReviewDialog
          trade={selectedTrade}
          open={openReview}
          onOpenChange={setOpenReview}
          // onSuccess={() => refetch()}
          onSuccess={() => {
            console.log("Review submitted");
          }}
        />
      </div>
    </div>
  );
};

export default UserDashboard;
